import { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Capacitor } from '@capacitor/core';
import { API_ENDPOINTS } from '@caro/shared/constants';
import type { AutoProvisionResult } from '@caro/shared/types';
import { useAuth } from './useAuth';
import { KeepAliveBridge } from '../native/KeepAliveBridge';
import { logEvent } from '../utils/debugLog';

/**
 * Zero-touch device auto-provisioning (R1).
 *
 * Spec: specs/20260602-resident-device-autoprovision/plan.md §5 R1
 *
 * Device identity keys on the Hexnode UDID. It is resolved from three sources,
 * in order:
 *   1. the managed-launch URL param (`?hexnodeUDID=<udid>`) — the web/kiosk path;
 *   2. the persisted `sessionStorage` value (survives SPA navigation / refresh);
 *   3. native Managed App Configuration (`RestrictionsManager`, read via
 *      `KeepAliveBridge.getDeviceIds().managedUdid`) — the APK FLEET path, where
 *      Hexnode pushes `hexnodeUdid = %udid%` per device so there is no per-device
 *      build and no URL param. No-ops on web / when the MDM hasn't pushed it.
 * On cold start when no DeviceToken exists, a URL-supplied UDID is persisted to
 * `sessionStorage` and stripped from the URL via `history.replaceState` —
 * mirroring the public-alarm-token pattern in repo-root CLAUDE.md. It POSTs
 * `{ hexnodeUDID }` to `AUTH_AUTO_PROVISION`; on success it stores the minted
 * device JWT in the same auth store the qrActivate / AdminSetupPage flow uses
 * and routes to `/`.
 *
 * Fail-closed (no UDID present, or 404 / 409 / timeout) NEVER throws — it routes
 * to `/admin-setup` so the operator can complete the PIN fallback. AdminSetupPage
 * is unchanged.
 *
 * NOTE: this hook no longer depends on `NativeBridge.getDeviceInfo()` /
 * `@capacitor/device`; the UDID arrives via the managed-launch URL.
 */

export const HEXNODE_UDID_STORAGE_KEY = 'caro.hexnodeUDID';

export interface AutoProvisionDeps {
  /** True when a DeviceToken / access token is already stored. */
  hasToken: () => boolean;
  /** Raw URL query string, e.g. `window.location.search`. */
  getUrlSearch: () => string;
  /** Read the persisted UDID (sessionStorage). */
  getStored: (key: string) => string | null;
  /** Persist the UDID (sessionStorage). */
  setStored: (key: string, value: string) => void;
  /** Strip the `hexnodeUDID` param from the URL (history.replaceState). */
  stripUrl: () => void;
  /**
   * Read the Hexnode UDID delivered via native Managed App Configuration
   * (RestrictionsManager). Optional: absent in tests / on web. Resolves null
   * when not native or when the MDM has not pushed `hexnodeUdid`.
   */
  getManagedUdid?: () => Promise<string | null>;
  /** Optional diagnostic sink (in-app DebugOverlay). No-op in tests / when absent. */
  log?: (tag: string, data?: unknown) => void;
  /** POST helper (api.post). */
  post: <T>(url: string, body: unknown) => Promise<T>;
  /** Persist the minted device token (same store as qrActivate / admin flow). */
  storeToken: (token: string) => void;
  /**
   * Persist the user profile from the provision response. REQUIRED for the app
   * to become authenticated: the shared auth store only sets `isAuthenticated`
   * true via `setUser` (not `setAccessToken`), and a populated user also
   * suppresses the User-JWT `/me` hydration that a DeviceToken can't satisfy.
   */
  storeUser?: (user: unknown) => void;
  /** Router navigate. */
  navigate: (path: string) => void;
}

export interface AutoProvisionOutcome {
  provisioned: boolean;
  skipped?: boolean;
}

type AutoProvisionEnvelope = { success?: boolean; data?: AutoProvisionResult } & Partial<AutoProvisionResult>;

/**
 * Pure, dependency-injected core of the auto-provision flow. Exported so it can
 * be unit-tested without mounting React.
 */
export async function runAutoProvision(deps: AutoProvisionDeps): Promise<AutoProvisionOutcome> {
  // Whether the device is ALREADY authenticated. When true we run in "refresh"
  // mode: re-fetch the provisioning profile to pick up server-side changes
  // (UI language, building, extension) WITHOUT navigating, and — critically —
  // WITHOUT logging the device out if the refresh fails. When false this is a
  // cold start: success → '/', any failure → '/admin-setup' (manual PIN).
  const hadToken = deps.hasToken();
  let udidSource: 'url' | 'session' | 'managed' | 'none' = 'none';

  // 1. Read the UDID from the URL first; persist + strip when present.
  let hexnodeUDID: string | null = null;
  try {
    const params = new URLSearchParams(deps.getUrlSearch() || '');
    const fromUrl = params.get('hexnodeUDID');
    if (fromUrl && fromUrl.trim()) {
      hexnodeUDID = fromUrl.trim();
      udidSource = 'url';
      deps.setStored(HEXNODE_UDID_STORAGE_KEY, hexnodeUDID);
      deps.stripUrl();
    }
  } catch {
    // URL parsing failed — fall through to the persisted value.
  }

  // 2. Fall back to the persisted UDID (survives SPA navigation / refresh).
  if (!hexnodeUDID) {
    try {
      const cached = deps.getStored(HEXNODE_UDID_STORAGE_KEY);
      if (cached && cached.trim()) {
        hexnodeUDID = cached.trim();
        udidSource = 'session';
      }
    } catch {
      // storage unavailable — treat as no UDID.
    }
  }

  // 2b. FLEET path: read the Hexnode UDID delivered via native Managed App
  // Configuration. This is how the APK fleet provisions hands-free — Hexnode
  // pushes `hexnodeUdid = %udid%` per device; no URL param, no per-device build.
  // No-ops on web and when the MDM hasn't pushed the value (resolves empty).
  if (!hexnodeUDID && deps.getManagedUdid) {
    try {
      const managed = await deps.getManagedUdid();
      if (managed && managed.trim()) {
        hexnodeUDID = managed.trim();
        udidSource = 'managed';
        deps.setStored(HEXNODE_UDID_STORAGE_KEY, hexnodeUDID);
      }
    } catch {
      // native bridge unavailable — treat as no UDID.
    }
  }

  deps.log?.('provision.resolve', { source: udidSource, udid: hexnodeUDID, hadToken });

  // 3. No UDID anywhere.
  if (!hexnodeUDID) {
    deps.log?.('provision.noUdid', { hadToken, action: hadToken ? 'keep-session' : 'admin-setup' });
    // Cold start with no UDID → manual PIN flow. Already-authed (no UDID to
    // refresh with, e.g. an email/password session) → leave the session alone.
    if (!hadToken) deps.navigate('/admin-setup');
    return { provisioned: false };
  }

  try {
    deps.log?.('provision.request', { endpoint: API_ENDPOINTS.AUTH_AUTO_PROVISION, udid: hexnodeUDID });
    const res = await deps.post<AutoProvisionEnvelope>(API_ENDPOINTS.AUTH_AUTO_PROVISION, {
      hexnodeUDID,
    });

    const result = (res?.data || res) as (AutoProvisionResult & { user?: unknown }) | undefined;
    const token = result?.deviceToken;

    if (!token) {
      deps.log?.('provision.noToken', { hadToken, action: hadToken ? 'keep-session' : 'admin-setup' });
      if (!hadToken) deps.navigate('/admin-setup');
      return { provisioned: false };
    }
    deps.log?.('provision.success', {
      reauth: hadToken,
      extension: (result as { extension?: unknown })?.extension,
      person: (result as { person?: { id?: unknown } })?.person?.id,
    });

    // Store the (possibly refreshed) token + profile. setUser flips
    // `isAuthenticated` true and refreshes language/building/etc.
    deps.storeToken(token);
    if (result?.user && deps.storeUser) {
      deps.storeUser(result.user);
    }
    // Only navigate on a cold start; a refresh must not yank the user around.
    if (!hadToken) deps.navigate('/');
    return { provisioned: true };
  } catch (e) {
    // Surface the server's failure detail (status + reason) so the DebugOverlay
    // shows WHY provisioning failed (e.g. 404 no_match = UDID not in the DB).
    const err = e as { message?: string; response?: { status?: number; data?: { reason?: string; message?: string } } };
    deps.log?.('provision.error', {
      status: err?.response?.status,
      reason: err?.response?.data?.reason,
      message: err?.response?.data?.message || err?.message,
      hadToken,
      action: hadToken ? 'keep-session' : 'admin-setup',
    });
    // Cold start failure → manual PIN fallback. Refresh failure (we already had
    // a valid session) → keep the existing session; do NOT log out / redirect.
    if (!hadToken) deps.navigate('/admin-setup');
    return { provisioned: false, skipped: hadToken };
  }
}

/**
 * React hook: runs auto-provision once on cold start when unauthenticated.
 * Wire it into the boot sequence before the `/login` redirect (App.tsx).
 */
// MODULE-LEVEL guard: auto-provision must run at most ONCE per page load, even
// if AutoProvisionBoot unmounts/remounts (e.g. a transient redirect to /login).
// A per-component ref resets on remount and caused a tight re-provision loop
// (~1 req/s) when the session briefly dropped. This survives remounts; a real
// cold start is a fresh page load, which resets it.
let autoProvisionAttempted = false;

export function useAutoProvision(): void {
  const { api, accessToken, setAccessToken, setUser } = useAuth();
  const navigate = useNavigate();
  const ranRef = useRef(false);

  useEffect(() => {
    if (ranRef.current || autoProvisionAttempted) return;
    // Runs once per page load (module guard survives remounts). On a cold start
    // (no token) it provisions; when already authenticated it REFRESHES the
    // profile (language/building/extension) if a hexnodeUDID is available —
    // runAutoProvision no-ops safely when there's a token but no UDID.
    ranRef.current = true;
    autoProvisionAttempted = true;

    void runAutoProvision({
      log: logEvent,
      hasToken: () => Boolean(accessToken),
      getUrlSearch: () => (typeof window !== 'undefined' ? window.location.search : ''),
      getStored: (key) => {
        try {
          return window.sessionStorage.getItem(key);
        } catch {
          return null;
        }
      },
      setStored: (key, value) => {
        try {
          window.sessionStorage.setItem(key, value);
        } catch {
          // private mode / storage disabled — UDID stays in memory for this run.
        }
      },
      stripUrl: () => {
        try {
          const params = new URLSearchParams(window.location.search);
          params.delete('hexnodeUDID');
          const qs = params.toString();
          const cleanUrl = window.location.pathname + (qs ? `?${qs}` : '') + window.location.hash;
          window.history.replaceState({}, '', cleanUrl);
        } catch {
          // history API unavailable — leave the URL as-is.
        }
      },
      // PRODUCTION-SAFETY: the native managed-config path is wired ONLY inside
      // the APK. On the browser fleet (Hexnode Website Kiosk / RPi Chromium)
      // `isNativePlatform()` is false, so this dep is `undefined` and step 2b in
      // runAutoProvision is structurally unreachable — the URL `?hexnodeUDID=`
      // path those 800 production devices use is left completely untouched.
      getManagedUdid: Capacitor.isNativePlatform()
        ? async () => {
            try {
              const ids = await KeepAliveBridge.getDeviceIds();
              return ids.managedUdid || null;
            } catch {
              return null;
            }
          }
        : undefined,
      post: (url, body) => api.post(url, body),
      storeToken: (token) => {
        // The resident app uses the device JWT as its access token for
        // Authorization: Bearer (same store qrActivate / admin-setup write to).
        setAccessToken(token);
      },
      storeUser: (user) => {
        // setUser flips isAuthenticated → true and suppresses the /me hydration
        // that a DeviceToken can't satisfy. Without this the app bounces to /login.
        setUser(user as any);
      },
      navigate: (path) => navigate(path),
    });
  }, [api, accessToken, setAccessToken, setUser, navigate]);
}
